import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ChevronDown,
  CheckCircle2,
  Sparkles,
  CalendarDays,
  Bell,
  FileText,
  HandHeart,
  MessageCircle,
  ClipboardList,
  HelpCircle,
  Languages,
  Heart,
  Loader2,
  Map,
  Syringe,
  Pill,
  X,
} from "lucide-react";
import AttendWithMeModal from "../components/AttendWithMeModal";
import * as EN from "../data/roadmapContent";
import * as HT from "../data/roadmapContent.ht";
import * as FR from "../data/roadmapContent.fr";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getClientToken() {
  try {
    return localStorage.getItem("soarlife-client-token");
  } catch {
    return null;
  }
}

function ScoreRing({ score, parts, ui }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, score)) / 100;
  return (
    <div className="flex items-center gap-5" data-testid="journey-score">
      <div className="relative w-24 h-24 flex-shrink-0">
        <svg viewBox="0 0 80 80" className="w-24 h-24 -rotate-90">
          <circle cx="40" cy="40" r={r} fill="none" stroke="#E7E2D8" strokeWidth="8" />
          <circle
            cx="40"
            cy="40"
            r={r}
            fill="none"
            stroke="#115E59"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - pct)}
            className="transition-all duration-700"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-serif text-2xl text-[#115E59] leading-none">{score}</span>
          <span className="text-[9px] uppercase tracking-[0.2em] text-[#899480]">/ 100</span>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold mb-1.5">
          {ui.scoreTitle}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {parts.map((p) => (
            <div key={p.key} className="flex items-center gap-1.5 text-[11px]">
              <CheckCircle2
                size={11}
                className={p.earned ? "text-[#115E59]" : "text-[#BCB3A6]"}
              />
              <span className={p.earned ? "text-[#3F5043]" : "text-[#899480]"}>
                {ui.scoreParts[p.key] || p.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ChecklistItem({ item, checked, onToggle }) {
  return (
    <button
      onClick={() => onToggle(item.key, !checked)}
      data-testid={`checklist-${item.key}`}
      role="checkbox"
      aria-checked={checked}
      className={`flex items-center gap-3 w-full text-left px-4 py-2.5 rounded-2xl transition-colors ${
        checked
          ? "bg-[#115E59]/10 text-[#115E59]"
          : "bg-white text-[#3F5043] hover:bg-[#115E59]/5"
      }`}
    >
      <span
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
          checked ? "bg-[#115E59] border-[#115E59]" : "border-[#BCB3A6]"
        }`}
      >
        {checked && <CheckCircle2 size={13} className="text-[#FFFFF8]" />}
      </span>
      <span className={`text-sm ${checked ? "line-through opacity-70" : ""}`}>
        {item.label}
      </span>
    </button>
  );
}

function QuestionGenerator({ topic, pathway, authHeaders, onGenerated, ui }) {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState(null);

  const generate = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/roadmap/questions`,
        { topic, pathway },
        { headers: authHeaders(), withCredentials: true }
      );
      setQuestions(res.data.questions);
      onGenerated?.();
    } catch {
      setQuestions([ui.couldNotGenerate]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3">
      <button
        onClick={generate}
        data-testid={`qg-${topic.slice(0, 20).replace(/\s/g, "-")}`}
        disabled={loading}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#115E59] text-[#FFFFF8] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#0D4A46] transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
        {ui.generateQuestions}
      </button>
      {questions && (
        <div className="mt-3 bg-[#FFFFF8] border border-[#D4AF37]/40 rounded-2xl p-4" data-testid="qg-results">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#875F45] font-semibold mb-2">
            {ui.questionsForProvider}
          </div>
          <ul className="space-y-1.5">
            {questions.map((q, i) => (
              <li key={i} className="text-sm text-[#3F5043] flex gap-2">
                <span className="text-[#D4AF37]">•</span>
                {q}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function EducationBlock({ sectionId, pathway, checklist, onToggle, authHeaders, onGenerated, ui, sections }) {
  const s = sections[sectionId];
  if (!s) return null;
  return (
    <div className="mt-4 bg-[#FFFFF8] border border-[#115E59]/15 rounded-2xl p-5" data-testid={`edu-${sectionId}`}>
      <div className="font-serif text-xl text-[#115E59] mb-2">{s.title}</div>
      {s.intro && <p className="text-sm text-[#3F5043]/80 leading-relaxed mb-3">{s.intro}</p>}
      {s.paragraphs?.map((p, i) => (
        <p key={i} className="text-sm text-[#3F5043]/80 leading-relaxed mb-2">{p}</p>
      ))}
      {s.groups?.map((g, i) => (
        <div key={i} className="mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#875F45] font-semibold mb-1.5">
            {g.label}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {g.items.map((it) =>
              typeof it === "string" ? (
                <span
                  key={it}
                  className="text-xs bg-white border border-[#115E59]/20 text-[#3F5043] px-3 py-1.5 rounded-full"
                >
                  {it}
                </span>
              ) : (
                <div
                  key={it.name}
                  className="w-full bg-white border border-[#115E59]/20 rounded-2xl px-4 py-2.5"
                >
                  <span className="text-sm font-medium text-[#115E59]">{it.name}</span>
                  <span className="text-sm text-[#3F5043]/75"> — {it.desc}</span>
                </div>
              )
            )}
          </div>
        </div>
      ))}
      {s.notes?.map((n, i) => (
        <div key={i} className="flex gap-2.5 mb-2">
          <div className="w-1 rounded-full bg-[#D4AF37] flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-[#115E59]">{n.heading}: </span>
            <span className="text-sm text-[#3F5043]/80">{n.text}</span>
          </div>
        </div>
      ))}
      {s.soarlifeProvides && (
        <div className="mt-3 bg-[#115E59]/5 border border-[#115E59]/15 rounded-2xl p-4" data-testid="soarlife-provides">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#115E59] font-semibold mb-1.5">
            Soarlife provides
          </div>
          <div className="flex flex-wrap gap-1.5">
            {s.soarlifeProvides.map((p) => (
              <span
                key={p}
                className="text-xs bg-white border border-[#D4AF37]/40 text-[#3F5043] px-3 py-1.5 rounded-full"
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}
      {s.suggestedQuestions && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#875F45] font-semibold mb-1.5">
            {ui.suggestedQuestionsLabel}
          </div>
          <ul className="space-y-1">
            {s.suggestedQuestions.map((q, i) => (
              <li key={i} className="text-sm text-[#3F5043]/80 flex gap-2">
                <span className="text-[#D4AF37]">•</span>
                {q}
              </li>
            ))}
          </ul>
        </div>
      )}
      {s.checklist && (
        <div className="mt-3 space-y-1.5">
          {s.checklist.map((item) => (
            <ChecklistItem
              key={item.key}
              item={item}
              checked={!!checklist[item.key]}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
      <QuestionGenerator
        topic={s.title}
        pathway={pathway}
        authHeaders={authHeaders}
        onGenerated={onGenerated}
        ui={ui}
      />
    </div>
  );
}

function TimelineStep({ step, index, total, pathway, checklist, onToggle, authHeaders, onGenerated, ui, sections }) {
  const [open, setOpen] = useState(index === 0);
  const [showGuide, setShowGuide] = useState(false);
  return (
    <div className="relative pl-10 pb-6 last:pb-0" data-testid={`timeline-step-${index}`}>
      {index < total - 1 && (
        <div className="absolute left-[15px] top-8 bottom-0 w-px bg-[#115E59]/20" />
      )}
      <div
        className={`absolute left-0 top-1 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
          open ? "bg-[#115E59] text-[#FFFFF8]" : "bg-white border-2 border-[#115E59]/30 text-[#115E59]"
        }`}
      >
        {index + 1}
      </div>
      <div className="bg-white border border-[#115E59]/15 rounded-3xl overflow-hidden">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
          data-testid={`timeline-toggle-${index}`}
        >
          <div>
            <div className="font-serif text-lg text-[#115E59] leading-tight">{step.title}</div>
            <div className="text-xs text-[#899480] mt-0.5">{step.summary}</div>
          </div>
          <ChevronDown
            size={18}
            className={`text-[#899480] transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
          />
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <div className="px-5 pb-5 border-t border-[#115E59]/10 pt-4">
                <ul className="space-y-2 mb-2">
                  {step.details.map((d, i) => (
                    <li key={i} className="text-sm text-[#3F5043]/85 leading-relaxed flex gap-2">
                      <span className="text-[#D4AF37] mt-0.5">•</span>
                      {d}
                    </li>
                  ))}
                </ul>
                {step.medicationCards && (
                  <div className="grid sm:grid-cols-3 gap-2 mb-2">
                    {step.medicationCards.map((m) => (
                      <div
                        key={m.name}
                        className="bg-[#FFFFF8] border border-[#D4AF37]/40 rounded-2xl px-4 py-3"
                      >
                        <div className="text-sm font-medium text-[#115E59]">{m.name}</div>
                        <div className="text-xs text-[#3F5043]/70 mt-0.5 leading-relaxed">{m.desc}</div>
                      </div>
                    ))}
                  </div>
                )}
                {step.medicationCards && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setShowGuide(true)}
                      data-testid={`watch-injection-guide-${index}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#115E59] text-[#FFFFF8] px-3.5 py-2 rounded-full hover:bg-[#0D4A46] transition-colors"
                    >
                      <Syringe size={12} className="text-[#D4AF37]" /> Watch: Injection Guide (11 min)
                    </button>
                    <Link
                      to="/portal#my-medications"
                      data-testid={`med-reminders-link-${index}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-[#115E59]/30 text-[#115E59] px-3.5 py-2 rounded-full hover:bg-[#115E59]/5 transition-colors"
                    >
                      <Pill size={12} /> Set medication reminders
                    </Link>
                  </div>
                )}
                {showGuide && (
                  <div
                    data-testid="roadmap-guide-modal"
                    className="fixed inset-0 z-50 bg-[#0D4A46]/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setShowGuide(false)}
                  >
                    <div
                      className="w-full max-w-3xl bg-[#FFFFF8] rounded-3xl overflow-hidden shadow-2xl"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between px-5 py-4 bg-[#115E59]">
                        <div>
                          <div className="font-serif text-lg text-[#FFFFF8]">Fertility Medication Injections</div>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                            Storage · Preparation · Administration — educational only
                          </div>
                        </div>
                        <button
                          onClick={() => setShowGuide(false)}
                          data-testid="roadmap-guide-close"
                          className="w-9 h-9 rounded-full bg-[#FFFFF8]/10 text-[#FFFFF8] flex items-center justify-center hover:bg-[#FFFFF8]/20"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <video
                        data-testid="roadmap-guide-video"
                        src="/videos/injection-guide.mp4"
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full aspect-video bg-black"
                      />
                    </div>
                  </div>
                )}
                {step.extraGroups?.map((g, gi) => (
                  <div key={gi} className="mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#875F45] font-semibold mb-1.5">
                      {g.label}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.items.map((it) => (
                        <span
                          key={it}
                          className="text-xs bg-white border border-[#115E59]/20 text-[#3F5043] px-3 py-1.5 rounded-full"
                        >
                          {it}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                {step.education?.map((eid) => (
                  <EducationBlock
                    key={eid}
                    sectionId={eid}
                    pathway={pathway}
                    checklist={checklist}
                    onToggle={onToggle}
                    authHeaders={authHeaders}
                    onGenerated={onGenerated}
                    ui={ui}
                    sections={sections}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function Roadmap() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [denied, setDenied] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [score, setScore] = useState(null);
  const [appts, setAppts] = useState([]);
  const [attendOpen, setAttendOpen] = useState(false);
  const [checkinMood, setCheckinMood] = useState(0);
  const [checkinNote, setCheckinNote] = useState("");
  const [checkinSaved, setCheckinSaved] = useState(false);
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("soarlife-roadmap-lang") || "en";
    } catch {
      return "en";
    }
  });

  const B = lang === "ht" ? HT : lang === "fr" ? FR : EN;
  const ui = B.UI;

  const LANG_CYCLE = { en: "ht", ht: "fr", fr: "en" };
  const LANG_NEXT_LABEL = { en: "Kreyòl", ht: "Français", fr: "English" };

  const toggleLang = () => {
    const next = LANG_CYCLE[lang] || "en";
    setLang(next);
    try {
      localStorage.setItem("soarlife-roadmap-lang", next);
    } catch {}
  };

  const authHeaders = () => {
    const t = getClientToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadAll = async () => {
    try {
      const [r, s, a] = await Promise.all([
        axios.get(`${API}/roadmap`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/roadmap/score`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/appointments`, { headers: authHeaders(), withCredentials: true }),
      ]);
      setRoadmap(r.data);
      setScore(s.data);
      setAppts(a.data);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const t = getClientToken();
        const res = await axios.get(`${API}/clients/me`, {
          headers: t ? { Authorization: `Bearer ${t}` } : {},
          withCredentials: true,
        });
        if (!res.data?.access_granted) {
          setDenied(true);
          return;
        }
        setClient(res.data);
        await loadAll();
      } catch {
        navigate("/community");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveState = async (next) => {
    setRoadmap(next);
    try {
      await axios.put(
        `${API}/roadmap`,
        { pathway: next.pathway, checklist: next.checklist },
        { headers: authHeaders(), withCredentials: true }
      );
      const s = await axios.get(`${API}/roadmap/score`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setScore(s.data);
    } catch {}
  };

  const toggleCheck = (key, val) => {
    saveState({ ...roadmap, checklist: { ...roadmap.checklist, [key]: val } });
  };

  const selectPathway = (id) => {
    saveState({ ...roadmap, pathway: id });
  };

  const refreshScore = async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/roadmap`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/roadmap/score`, { headers: authHeaders(), withCredentials: true }),
      ]);
      setRoadmap(r.data);
      setScore(s.data);
    } catch {}
  };

  const submitCheckin = async () => {
    if (!checkinMood) return;
    try {
      await axios.post(
        `${API}/roadmap/checkin`,
        { mood: checkinMood, note: checkinNote },
        { headers: authHeaders(), withCredentials: true }
      );
      setCheckinMood(0);
      setCheckinNote("");
      setCheckinSaved(true);
      setTimeout(() => setCheckinSaved(false), 2500);
      refreshScore();
    } catch {}
  };

  if (denied) {
    return (
      <div className="min-h-screen bg-[#FFFFF8] flex items-center justify-center p-5" data-testid="roadmap-denied">
        <div className="max-w-sm text-center">
          <div className="font-serif text-3xl text-[#115E59] mb-3">{ui.deniedTitle}</div>
          <p className="text-sm text-[#3F5043]/70 mb-5">{ui.deniedBody}</p>
          <Link
            to="/community"
            className="inline-flex rounded-full px-6 py-3 bg-[#115E59] text-[#FFFFF8] text-sm font-medium"
          >
            {ui.deniedCta}
          </Link>
        </div>
      </div>
    );
  }

  if (!client || !roadmap) {
    return (
      <div className="min-h-screen bg-[#FFFFF8] flex items-center justify-center" data-testid="roadmap-loading">
        <Loader2 size={22} className="animate-spin text-[#115E59]" />
      </div>
    );
  }

  const pathway = B.PATHWAYS.find((p) => p.id === roadmap.pathway);
  const allChecklistItems = pathway
    ? [...pathway.checklist, ...(pathway.prepareChecklist?.items || [])]
    : [];
  const doneCount = allChecklistItems.filter((i) => roadmap.checklist[i.key]).length;
  const progress = allChecklistItems.length
    ? Math.round((doneCount / allChecklistItems.length) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[#FFFFF8]" data-testid="roadmap-page">
      {/* Header */}
      <header className="bg-[#115E59] text-[#FFFFF8]">
        <div className="max-w-5xl mx-auto px-5 py-6">
          <div className="flex items-center justify-between mb-5">
            <Link
              to="/community"
              data-testid="roadmap-back"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FFFFF8]/70 hover:text-[#FFFFF8]"
            >
              <ArrowLeft size={14} /> {ui.portal}
            </Link>
            <button
              onClick={toggleLang}
              data-testid="roadmap-lang-toggle"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
            >
              <Languages size={12} />
              {LANG_NEXT_LABEL[lang] || "English"}
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Map size={22} className="text-[#D4AF37]" />
            <div>
              <h1 className="font-serif italic text-3xl sm:text-4xl leading-tight">
                Soarlife Fertility Roadmap™
              </h1>
              <p className="text-xs uppercase tracking-[0.22em] text-[#FFFFF8]/60 mt-1">
                {ui.tagline}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-8">
        {/* Journey Score */}
        {score && (
          <div className="bg-white border border-[#115E59]/15 rounded-3xl p-6 mb-8">
            <ScoreRing score={score.score} parts={score.parts} ui={ui} />
          </div>
        )}

        {/* Pathway selection */}
        {!pathway ? (
          <div data-testid="pathway-picker">
            <div className="text-xs uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-2">
              {ui.choosePathway}
            </div>
            <h2 className="font-serif text-2xl text-[#115E59] mb-1">
              {ui.whereAreYou}, {client.full_name.split(" ")[0]}?
            </h2>
            <p className="text-sm text-[#3F5043]/70 mb-6">{ui.pickHint}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {B.PATHWAYS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => selectPathway(p.id)}
                  data-testid={`pathway-${p.id}`}
                  className="text-left p-5 rounded-3xl bg-white border border-[#115E59]/15 hover:border-[#D4AF37] hover:shadow-md transition-all group"
                >
                  <div className="font-serif text-lg text-[#115E59] leading-tight mb-1 group-hover:text-[#875F45] transition-colors">
                    {p.title}
                  </div>
                  <div className="text-xs text-[#899480] leading-relaxed">{p.tagline}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Active pathway header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#875F45] font-semibold mb-1">
                  {ui.yourPathway}
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-[#115E59]">{pathway.title}</h2>
              </div>
              <button
                onClick={() => saveState({ ...roadmap, pathway: null })}
                data-testid="change-pathway"
                className="text-xs uppercase tracking-[0.18em] text-[#899480] hover:text-[#115E59] underline underline-offset-4"
              >
                {ui.changePathway}
              </button>
            </div>

            {/* What is it */}
            {pathway.whatIsIt && (
              <div
                className="bg-[#115E59]/5 border-l-4 border-[#115E59] rounded-r-2xl px-5 py-4 mb-6"
                data-testid="what-is-it"
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#115E59] font-semibold mb-1">
                  {ui.whatIsIt}
                </div>
                <p className="text-sm text-[#3F5043]/85 leading-relaxed">{pathway.whatIsIt}</p>
              </div>
            )}

            {/* Monthly rhythm */}
            {pathway.whatToExpect && (
              <div
                className="bg-white border border-[#115E59]/15 rounded-3xl p-5 mb-6"
                data-testid="pathway-rhythm"
              >
                <div className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold mb-3">
                  {ui.whatToExpect}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {pathway.whatToExpect.map((g) => (
                    <div key={g.label}>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#875F45] font-semibold mb-1.5">
                        {g.label}
                      </div>
                      <ul className="space-y-1">
                        {g.items.map((it) => (
                          <li key={it} className="text-sm text-[#3F5043]/80 flex gap-2">
                            <span className="text-[#D4AF37]">•</span>
                            {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progress bar */}
            <div className="mb-8" data-testid="roadmap-progress">
              <div className="flex justify-between text-[11px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-1.5">
                <span>{ui.pathwayProgress}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 rounded-full bg-[#E7E2D8] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#115E59] to-[#D4AF37] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Timeline */}
              <div className="lg:col-span-2" id="what-to-expect">
                <div className="flex items-center gap-2 mb-4">
                  <ClipboardList size={15} className="text-[#D4AF37]" />
                  <span className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold">
                    {ui.whatToExpectSteps}
                  </span>
                </div>
                {pathway.steps.map((step, i) => (
                  <TimelineStep
                    key={i}
                    step={step}
                    index={i}
                    total={pathway.steps.length}
                    pathway={pathway.title}
                    checklist={roadmap.checklist}
                    onToggle={toggleCheck}
                    authHeaders={authHeaders}
                    onGenerated={refreshScore}
                    ui={ui}
                    sections={B.EDUCATION_SECTIONS}
                  />
                ))}

                {/* Pathway checklist */}
                <div className="mt-8 bg-[#115E59]/5 border border-[#115E59]/15 rounded-3xl p-5" data-testid="pathway-checklist">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold mb-3">
                    {pathway.title} · {ui.checklistSuffix}
                  </div>
                  <div className="space-y-1.5">
                    {pathway.checklist.map((item) => (
                      <ChecklistItem
                        key={item.key}
                        item={item}
                        checked={!!roadmap.checklist[item.key]}
                        onToggle={toggleCheck}
                      />
                    ))}
                  </div>
                </div>

                {/* Fixed doctor questions */}
                {pathway.doctorQuestions && (
                  <div
                    className="mt-8 bg-[#FFFFF8] border border-[#D4AF37]/40 rounded-3xl p-5"
                    data-testid="doctor-questions"
                  >
                    <div className="text-xs uppercase tracking-[0.22em] text-[#875F45] font-semibold mb-3">
                      {ui.doctorQuestions}
                    </div>
                    <ul className="space-y-1.5 mb-2">
                      {pathway.doctorQuestions.map((q) => (
                        <li key={q} className="text-sm text-[#3F5043]/85 flex gap-2">
                          <span className="text-[#D4AF37]">•</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                    <QuestionGenerator
                      topic={`${pathway.title} treatment`}
                      pathway={pathway.title}
                      authHeaders={authHeaders}
                      onGenerated={refreshScore}
                      ui={ui}
                    />
                  </div>
                )}

                {/* What to prepare */}
                {pathway.prepareChecklist && (
                  <div
                    className="mt-6 bg-white border border-[#115E59]/15 rounded-3xl p-5"
                    data-testid="prepare-checklist"
                  >
                    <div className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold mb-3">
                      {pathway.prepareChecklist.title}
                    </div>
                    <div className="space-y-1.5">
                      {pathway.prepareChecklist.items.map((item) => (
                        <ChecklistItem
                          key={item.key}
                          item={item}
                          checked={!!roadmap.checklist[item.key]}
                          onToggle={toggleCheck}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Shared education always available */}
                <div className="mt-8">
                  <EducationBlock
                    sectionId="evaluation"
                    pathway={pathway.title}
                    checklist={roadmap.checklist}
                    onToggle={toggleCheck}
                    authHeaders={authHeaders}
                    onGenerated={refreshScore}
                    ui={ui}
                    sections={B.EDUCATION_SECTIONS}
                  />
                  <EducationBlock
                    sectionId="amh"
                    pathway={pathway.title}
                    checklist={roadmap.checklist}
                    onToggle={toggleCheck}
                    authHeaders={authHeaders}
                    onGenerated={refreshScore}
                    ui={ui}
                    sections={B.EDUCATION_SECTIONS}
                  />
                </div>
              </div>

              {/* Action Center */}
              <aside className="space-y-5">
                <div className="bg-[#115E59] text-[#FFFFF8] rounded-3xl p-5" data-testid="action-center">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-semibold mb-4">
                    {ui.actionCenter}
                  </div>
                  <div className="space-y-2">
                    <a href="#what-to-expect" data-testid="ac-expect" className="flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-2xl px-4 py-3 text-sm transition-colors">
                      <ClipboardList size={15} className="text-[#D4AF37]" /> {ui.acExpect}
                    </a>
                    <a href="#questions" data-testid="ac-questions" className="flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-2xl px-4 py-3 text-sm transition-colors">
                      <HelpCircle size={15} className="text-[#D4AF37]" /> {ui.acQuestions}
                    </a>
                    <a href="#appointments" data-testid="ac-appointments" className="flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-2xl px-4 py-3 text-sm transition-colors">
                      <CalendarDays size={15} className="text-[#D4AF37]" /> {ui.acAppointments}
                    </a>
                    <a href="#reminders" data-testid="ac-reminders" className="flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-2xl px-4 py-3 text-sm transition-colors">
                      <Bell size={15} className="text-[#D4AF37]" /> {ui.acReminders}
                    </a>
                    <Link to="/community#my-records" data-testid="ac-upload" className="flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-2xl px-4 py-3 text-sm transition-colors">
                      <FileText size={15} className="text-[#D4AF37]" /> {ui.acUpload}
                    </Link>
                    <button onClick={() => setAttendOpen(true)} data-testid="ac-attend" className="w-full flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-2xl px-4 py-3 text-sm transition-colors text-left">
                      <HandHeart size={15} className="text-[#D4AF37]" /> {ui.acAttend}
                    </button>
                    <Link to="/chat" data-testid="ac-ai" className="flex items-center gap-3 bg-[#D4AF37] hover:bg-[#C19A2B] text-[#115E59] font-medium rounded-2xl px-4 py-3 text-sm transition-colors">
                      <Sparkles size={15} /> {ui.acAI}
                    </Link>
                  </div>
                  <div className="mt-5 pt-4 border-t border-white/15">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFFFF8]/60 mb-2">
                      {ui.supportOptions}
                    </div>
                    <ul className="space-y-1.5">
                      {B.ACTION_CENTER.supportOptions.map((o) => (
                        <li key={o} className="text-xs text-[#FFFFF8]/85 flex gap-2">
                          <span className="text-[#D4AF37]">•</span>
                          {o}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {B.ACTION_CENTER.languages.map((l) => (
                        <span key={l} className="text-[10px] uppercase tracking-[0.12em] text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-2.5 py-0.5">
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Appointments */}
                <div id="appointments" className="bg-white border border-[#115E59]/15 rounded-3xl p-5" data-testid="roadmap-appointments">
                  <div className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold mb-3">
                    {ui.upcomingAppointments}
                  </div>
                  {appts.length === 0 ? (
                    <p className="text-sm text-[#899480]">{ui.noAppointments}</p>
                  ) : (
                    <div className="space-y-2">
                      {appts.map((a) => (
                        <div key={a.id} className="bg-[#FFFFF8] border border-[#115E59]/10 rounded-2xl px-4 py-3">
                          <div className="text-sm font-medium text-[#3F5043]">{a.title}</div>
                          <div className="text-[11px] text-[#899480] mt-0.5">
                            {a.date?.replace("T", " · ")}
                            {a.location ? ` · ${a.location}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Reminders */}
                <div id="reminders" className="bg-white border border-[#115E59]/15 rounded-3xl p-5" data-testid="roadmap-reminders">
                  <div className="flex items-center gap-2 mb-3">
                    <Bell size={14} className="text-[#D4AF37]" />
                    <span className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold">
                      {ui.automaticReminders}
                    </span>
                  </div>
                  <p className="text-xs text-[#3F5043]/70 leading-relaxed mb-3">{ui.remindersIntro}</p>
                  <div className="space-y-1 text-xs text-[#3F5043]/80">
                    {ui.reminderMilestones.map(([d, t]) => (
                      <div key={d} className="flex gap-2">
                        <span className="text-[#D4AF37] font-semibold w-14 flex-shrink-0">{d}</span>
                        {t}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Wellness check-in */}
                <div className="bg-white border border-[#115E59]/15 rounded-3xl p-5" data-testid="wellness-checkin">
                  <div className="flex items-center gap-2 mb-3">
                    <Heart size={14} className="text-[#D4AF37]" />
                    <span className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold">
                      {ui.wellnessTitle}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {ui.moods.map((m, i) => (
                      <button
                        key={m}
                        onClick={() => setCheckinMood(i + 1)}
                        data-testid={`mood-${i + 1}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          checkinMood === i + 1
                            ? "bg-[#115E59] text-[#FFFFF8] border-[#115E59]"
                            : "border-[#BCB3A6]/60 text-[#3F5043] hover:border-[#115E59]"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                  <textarea
                    data-testid="checkin-note"
                    rows={2}
                    value={checkinNote}
                    onChange={(e) => setCheckinNote(e.target.value)}
                    placeholder={ui.checkinPlaceholder}
                    className="w-full bg-[#FFFFF8] border border-[#115E59]/15 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#115E59]/40 resize-none mb-2"
                  />
                  <button
                    onClick={submitCheckin}
                    data-testid="checkin-submit"
                    disabled={!checkinMood}
                    className="w-full rounded-full px-4 py-2.5 bg-[#115E59] text-[#FFFFF8] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#0D4A46] transition-colors disabled:opacity-50"
                  >
                    {checkinSaved ? ui.checkinSaved : ui.saveCheckin}
                  </button>
                </div>

                {/* My questions */}
                <div id="questions" className="bg-white border border-[#115E59]/15 rounded-3xl p-5" data-testid="my-questions">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle size={14} className="text-[#D4AF37]" />
                    <span className="text-xs uppercase tracking-[0.22em] text-[#115E59] font-semibold">
                      {ui.myQuestions}
                    </span>
                  </div>
                  {roadmap.questions?.length ? (
                    <div className="space-y-3">
                      {roadmap.questions.slice(-3).reverse().map((q) => (
                        <div key={q.id}>
                          <div className="text-[10px] uppercase tracking-[0.18em] text-[#875F45] font-semibold mb-1">
                            {q.topic}
                          </div>
                          <ul className="space-y-1">
                            {q.questions.slice(0, 3).map((qq, i) => (
                              <li key={i} className="text-xs text-[#3F5043]/80 flex gap-2">
                                <span className="text-[#D4AF37]">•</span>
                                {qq}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-[#899480]">{ui.noQuestions}</p>
                  )}
                </div>
              </aside>
            </div>
          </>
        )}
      </main>

      <AttendWithMeModal open={attendOpen} onClose={() => setAttendOpen(false)} />
    </div>
  );
}
